import React from 'react';
import styled from 'astroturf/react';

type Props = {
  paperTitle: string;
  authorName?: string;
  thumbnailUrl?: string;
  year?: number;
  viewCount?: number;
};

const FALLBACK_THUMBNAIL =
  'https://a.academia-assets.com/images/blank-paper.jpg';

// Title-cases ALL-CAPS author names; leaves already-cased names alone.
function normalizeAllCapsName(name: string): string {
  if (name !== name.toLocaleUpperCase()) return name;
  return name
    .toLocaleLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toLocaleUpperCase() + word.slice(1))
    .join(' ');
}

// "Your paper" card for the login modal's `headerVariant: 'yourPaperCard'`
// socket. Work-context props are optional; absent ones degrade gracefully.
export const YourPaperCard: React.FC<Props> = ({
  paperTitle,
  authorName,
  thumbnailUrl,
  year,
  viewCount,
}) => {
  const thumbSrc = thumbnailUrl || FALLBACK_THUMBNAIL;
  const normalizedAuthor = authorName
    ? normalizeAllCapsName(authorName)
    : undefined;
  const metaParts: React.ReactNode[] = [];
  if (normalizedAuthor) metaParts.push(`by ${normalizedAuthor}`);
  if (year != null) metaParts.push(String(year));
  if (viewCount != null && viewCount > 0) {
    // Suppress at 0: rendering "0 views" on a brand-new work is the
    // opposite of the trust signal this card is supposed to send.
    metaParts.push(`${viewCount} ${viewCount === 1 ? 'view' : 'views'}`);
  }

  return (
    <AnchoredWrapper>
      <Chip>Your paper</Chip>
      <CardBody>
        <Row>
          <Thumb>
            <img alt="" src={thumbSrc} />
          </Thumb>
          <Meta>
            <Title>{paperTitle}</Title>
            {metaParts.length > 0 && (
              <MetaLine>
                {metaParts.map((part, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <MetaSep>|</MetaSep>}
                    <MetaPart>{part}</MetaPart>
                  </React.Fragment>
                ))}
              </MetaLine>
            )}
          </Meta>
        </Row>
      </CardBody>
    </AnchoredWrapper>
  );
};

const AnchoredWrapper = styled.div`
  position: relative;
  border: 1px solid var(--primary-brand, #0645b1);
  border-radius: var(--border-radius-radius-md, 8px);
  box-shadow: 0 2px 6px rgba(6, 69, 177, 0.15);
  margin-top: var(--spacing-xs-12, 12px);
  margin-bottom: var(--spacing-xs-8, 8px);
`;

const Chip = styled.div`
  position: absolute;
  top: -14px;
  left: var(--spacing-xs-12, 12px);
  z-index: 1;
  pointer-events: none;
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 var(--spacing-xs-8, 8px);
  border-radius: var(--border-radius-radius-md, 8px);
  background: var(--primary-brand, #0645b1);
  color: var(--text-white, #ffffff);
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: 11px;
  font-weight: 500;
`;

const CardBody = styled.div`
  padding: var(--spacing-sm-24, 24px) var(--spacing-xs-16, 16px)
    var(--spacing-xs-16, 16px);
`;

const Row = styled.div`
  display: flex;
  gap: var(--spacing-xs-12, 12px);
  align-items: flex-start;
`;

const Thumb = styled.div`
  width: 50px;
  height: 72px;
  border: 1px solid var(--stroke-beige-light, #ccc9bc);
  background: var(--background-white, #ffffff);
  overflow: hidden;
  flex: 0 0 auto;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const Title = styled.div`
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: var(--type-body-sm-size, 14px);
  line-height: var(--type-body-sm-line-height, 20px);
  font-weight: 600;
  color: var(--text-primary, #141413);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const MetaLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: var(--type-body-xs-size, 12px);
  line-height: var(--type-body-xs-line-height, 18px);
  color: var(--text-secondary, #535366);
`;

const MetaPart = styled.span``;

const MetaSep = styled.span`
  color: var(--text-secondary, #535366);
`;
